/**
 * AttachmentsField — reusable Drag & Drop attachments picker.
 *
 * Used in: TaskCreateModal, TaskDetailSheet (edit mode), IdeaBankFormDialog.
 *
 * Behaviour:
 *   - Accepts images (JPG/PNG/GIF/WEBP), videos (MP4/MOV/AVI), and PDFs.
 *   - Multiple-select via the file input AND drag-drop on the dropzone.
 *   - Each file is uploaded to /api/media/upload immediately so the metadata
 *     (id, url, mime, thumbnail) is ready as soon as the task/idea form is
 *     submitted. Per-file progress is displayed.
 *   - Image / video previews render inline; PDFs show a file icon + name.
 *   - Each item has a remove button (X) that strips it from the array.
 *   - Returns the live array via `onChange`. Existing attachments passed in
 *     via `value` (e.g. when editing) are kept and rendered the same way.
 *
 * No file count limit. Server enforces size; see /api/media/upload.
 */
import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { FileText, X, Upload, Image as ImageIcon, Film, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { toast } from 'sonner';

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/x-msvideo,application/pdf';
const ACCEPT_TYPES_DISPLAY = 'Images (JPG, PNG, GIF, WEBP), Videos (MP4, MOV, AVI), PDF';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function attachmentKind(att) {
  const mime = (att?.mime || att?.content_type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/pdf') return 'pdf';
  return 'file';
}

function prettyBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/**
 * Uploads a single File to /api/media/upload and resolves with the
 * attachment metadata dict the task / idea expects.
 */
function uploadSingleFile({ file, workspaceId, token, onProgress }) {
  const form = new FormData();
  form.append('file', file);
  form.append('workspace_id', workspaceId);
  return axios
    .post(`${BACKEND}/api/media/upload`, form, {
      headers: {
        Authorization: `Bearer ${token}`,
        // axios sets the multipart boundary itself when given a FormData.
      },
      onUploadProgress: (e) => {
        if (!e.total) return;
        onProgress(Math.round((e.loaded * 100) / e.total));
      },
    })
    .then((res) => {
      const m = res.data || {};
      return {
        id: m.id,
        name: m.filename || file.name,
        url: m.public_url,
        mime: m.content_type,
        size: m.file_size,
        thumbnail_url: m.thumbnail_url || null,
        uploaded_by: null,   // server sets it on the media doc; we don't expose for task ACL — see backend RBAC.
        uploaded_at: m.created_at,
      };
    });
}

export function AttachmentsField({
  value = [],
  onChange,
  workspaceId,
  testIdPrefix = 'attachments',
  label = 'Attachments',
  disabled = false,
}) {
  const fileInputRef = useRef(null);
  const [pending, setPending] = useState([]);  // [{tmpId, name, progress, error?}]
  const [dragOver, setDragOver] = useState(false);
  const token = typeof window !== 'undefined' ? localStorage.getItem('stellar_token') : null;

  const addFiles = useCallback(async (fileList) => {
    if (!workspaceId) {
      toast.error('Pick a workspace before uploading attachments.');
      return;
    }
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const startedAt = Date.now();
    const tmpItems = files.map((f, i) => ({
      tmpId: `${startedAt}-${i}-${f.name}`,
      name: f.name,
      size: f.size,
      progress: 0,
      error: null,
    }));
    setPending((p) => [...p, ...tmpItems]);
    // Upload in parallel — small concurrency cap to avoid trashing the network.
    const POOL = 3;
    let cursor = 0;
    const next = async () => {
      while (cursor < files.length) {
        const idx = cursor++;
        const file = files[idx];
        const tmpId = tmpItems[idx].tmpId;
        try {
          const att = await uploadSingleFile({
            file, workspaceId, token,
            onProgress: (pct) => {
              setPending((p) => p.map((it) => (it.tmpId === tmpId ? { ...it, progress: pct } : it)));
            },
          });
          // Append to the form value and drop the pending row.
          onChange([...(value || []), ...[]].concat(att));
          setPending((p) => p.filter((it) => it.tmpId !== tmpId));
        } catch (e) {
          setPending((p) => p.map((it) => (it.tmpId === tmpId ? { ...it, error: 'Upload failed' } : it)));
          toast.error(`Failed to upload ${file.name}`);
        }
      }
    };
    // Spawn POOL workers.
    await Promise.all(Array.from({ length: Math.min(POOL, files.length) }, () => next()));
  }, [value, onChange, workspaceId, token]);

  const removeAt = (i) => {
    const next = (value || []).filter((_, j) => j !== i);
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between">
        <span className="text-[12px] text-foreground/70">{label}</span>
        {(value && value.length > 0) && (
          <span className="text-[10.5px] text-muted-foreground/50">
            {value.length} file{value.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* Dropzone */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
        className={`w-full rounded-lg border border-dashed px-3 py-4 text-left transition-colors ${dragOver ? 'border-gold bg-gold/10' : 'border-border/40 bg-foreground/[0.02] hover:border-gold/40 hover:bg-foreground/[0.04]'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid={`${testIdPrefix}-dropzone`}
      >
        <div className="flex items-center gap-2.5 text-[12.5px] text-foreground/80">
          <Upload className="h-4 w-4 text-gold shrink-0" />
          <span className="font-medium">Drop files here</span>
          <span className="text-muted-foreground/60">or click to browse</span>
        </div>
        <p className="text-[10.5px] text-muted-foreground/45 mt-1">{ACCEPT_TYPES_DISPLAY}. Up to 2 GB per file.</p>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          data-testid={`${testIdPrefix}-input`}
        />
      </button>

      {/* Pending (uploading) tiles */}
      {pending.length > 0 && (
        <div className="space-y-1.5">
          {pending.map((p) => (
            <div key={p.tmpId} className="flex items-center gap-2 px-2.5 py-2 rounded-md bg-foreground/[0.03] border border-border/30" data-testid={`${testIdPrefix}-pending`}>
              {p.error
                ? <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
                : <Loader2 className="h-4 w-4 animate-spin text-gold shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-[11.5px] text-foreground/85 truncate">{p.name}</p>
                <Progress value={p.progress} className="h-1 mt-1" />
              </div>
              <span className="text-[10.5px] text-muted-foreground/55 shrink-0 tabular-nums">
                {p.error ? 'failed' : `${p.progress}%`}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Selected tiles */}
      {(value || []).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid={`${testIdPrefix}-list`}>
          {value.map((att, i) => {
            // Tolerate legacy string entries (just URL strings, no metadata).
            if (typeof att === 'string') {
              return (
                <div key={`legacy-${i}`} className="relative rounded-md border border-border/30 bg-foreground/[0.03] px-2.5 py-2 flex items-center gap-2" data-testid={`${testIdPrefix}-item-${i}`}>
                  <FileText className="h-4 w-4 text-muted-foreground/70 shrink-0" />
                  <a href={att} target="_blank" rel="noopener noreferrer" className="text-[11px] text-gold hover:underline truncate flex-1">{att}</a>
                  {!disabled && (
                    <button type="button" onClick={() => removeAt(i)} className="shrink-0 text-muted-foreground/55 hover:text-red-400" aria-label="Remove" data-testid={`${testIdPrefix}-remove-${i}`}>
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            }
            const kind = attachmentKind(att);
            const previewUrl = att.thumbnail_url || att.url;
            return (
              <div key={att.id || i} className="relative group rounded-md border border-border/30 overflow-hidden bg-card" data-testid={`${testIdPrefix}-item-${i}`}>
                {/* Preview */}
                {kind === 'image' && previewUrl ? (
                  <img src={previewUrl} alt={att.name} loading="lazy" className="w-full aspect-video object-cover" />
                ) : kind === 'video' ? (
                  previewUrl && att.thumbnail_url ? (
                    <div className="relative w-full aspect-video">
                      <img src={previewUrl} alt={att.name} loading="lazy" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <Film className="h-7 w-7 text-white drop-shadow-md" />
                      </div>
                    </div>
                  ) : (
                    <div className="w-full aspect-video bg-foreground/[0.05] flex items-center justify-center"><Film className="h-7 w-7 text-muted-foreground/40" /></div>
                  )
                ) : (
                  <div className="w-full aspect-video bg-foreground/[0.05] flex items-center justify-center">
                    {kind === 'pdf' ? <FileText className="h-7 w-7 text-red-400/70" /> : <ImageIcon className="h-7 w-7 text-muted-foreground/40" />}
                  </div>
                )}
                <div className="px-2 py-1.5 flex items-center gap-1.5 border-t border-border/20">
                  <p className="text-[10.5px] truncate flex-1 text-foreground/85">{att.name}</p>
                  <span className="text-[9px] text-muted-foreground/45 tabular-nums shrink-0">{prettyBytes(att.size)}</span>
                </div>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/80 backdrop-blur border border-border/30 hover:bg-red-500/15 hover:border-red-500/40 text-foreground/70 hover:text-red-400 flex items-center justify-center opacity-80 hover:opacity-100"
                    aria-label="Remove attachment"
                    data-testid={`${testIdPrefix}-remove-${i}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AttachmentsField;
